import {
	RiAddFill,
	RiDownloadLine,
	RiFileTextLine
} from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { Button as NskButton } from "~/components/common/parts/button/Button";
import { DeleteMenuButton } from "~/components/common/parts/button/DeleteMenuButton";
import { EditMenuButton } from "~/components/common/parts/button/EditMenuButton";
import Loading from "~/components/common/parts/loading/Loading";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { UploadButton } from "~/components/common/upload/UploadButton";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import type {
	DealUploadFile,
	DealUploadFileUpdateParam
} from "~/models/DealUploadFile";
import { canDestroy } from "~/models/DealUploadFile";
import type { VendorSession } from "~/models/VendorSession";
import { isSuccess } from "~/utils/api/api_handler";
import { formatDate } from "~/utils/DateUtil";
import Styles from "./DealFileCard.module.scss";

type Props = {
	createPdf?: (() => Promise<NskResponse>) | null;
	deal: Deal;
	description?: string | null;
	downloadExcel?: (() => void) | null;
	files: readonly DealUploadFile[];
	fileType: string;
	handleDestroy?: ((file: DealUploadFile) => Promise<NskResponse>) | null;
	handleGetFilePath: (file: DealUploadFile) => string;
	handleMutate: () => Promise<void>;
	handleUpdate?:
		| ((dealOtherParam: DealUploadFileUpdateParam) => Promise<NskResponse>)
		| null;
	handleUpload?: ((file: File) => Promise<NskResponse>) | null;
	isVendor?: boolean;
	vendorSession?: VendorSession | null;
};

export const DealFileCard: React.FC<Props> = ({
	fileType,
	files,
	deal,
	handleGetFilePath,
	handleMutate,
	description = null,
	handleUpload = null,
	handleUpdate = null,
	createPdf = null,
	downloadExcel = null,
	handleDestroy = null,
	vendorSession = null,
	isVendor = false
}: Props) => {
	const { notify } = useNotification();
	const [deleteFile, setDeleteFile] = useState<DealUploadFile | null>(null);
	const [editFile, setEditFile] = useState<DealUploadFile | null>(null);
	const [optionalFilename, setOptionalFilename] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const onClickUpload = async (file: File): Promise<void> => {
		if (handleUpload === null) {
			return;
		}
		const result = await handleUpload(file);
		if (isSuccess(result)) {
			await handleMutate();
			notify({ content: "登録しました。", variant: "success" });
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	};
	const onClickCreatePdf = async (): Promise<void> => {
		setIsLoading(true);

		const result = await createPdf!();
		if (isSuccess(result)) {
			await handleMutate();
			notify({ content: "作成しました。", variant: "success" });
		} else {
			notify({
				content: result.message ?? "エラーが発生しました",
				variant: "error"
			});
		}
		setIsLoading(false);
	};
	const acceptDestroy = async (): Promise<void> => {
		if (handleDestroy === null || deleteFile === null) {
			return;
		}
		const result = await handleDestroy(deleteFile);
		if (isSuccess(result)) {
			await handleMutate();
			setDeleteFile(null);
			notify({ content: "削除しました。", variant: "success" });
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	};
	const acceptUpdate = async (): Promise<void> => {
		if (handleUpdate === null || editFile === null) {
			return;
		}
		const result = await handleUpdate({
			id: editFile.id,
			optionalFilename
		});
		if (isSuccess(result)) {
			await handleMutate();
			setEditFile(null);
			notify({ content: "変更しました。", variant: "success" });
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	};
	const clearEditFile = (): void => {
		setEditFile(null);
	};
	const onChangeOptionalFilename = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			setOptionalFilename(e.target.value.length === 0 ? null : e.target.value);
		},
		[setOptionalFilename]
	);
	const renderRow = (file: DealUploadFile, index: number): ReactNode => {
		const extension = file.fileName.split(".").pop();
		const fileAddName = ((): string | null => {
			if (file.staff !== null) {
				return file.staff.name;
			}
			if (file.vendorUser !== null) {
				return file.vendorUser.name;
			}
			return null;
		})();
		const displayFilename = ((): string => {
			if (file.optionalFilename !== null) {
				return `${index + 1}_${file.optionalFilename}`;
			}
			if (file.originalFilename !== null) {
				return `${index + 1}_${file.originalFilename}`;
			}
			return `${index + 1}_${deal.name}_${
				deal.roomNumber
			}_${fileType}.${extension}`;
		})();
		const canEdit = ((): boolean => {
			if (!isVendor) {
				return true;
			}
			if (
				vendorSession !== null &&
				vendorSession.vendorUser?.id === file.vendorUser?.id
			) {
				return true;
			}
			return false;
		})();
		return (
			<div className={Styles.FileRow} key={file.id}>
				<div className={Styles.FileContent}>
					<a
						className={Styles.FileLink}
						href={handleGetFilePath(file)}
						rel="noreferrer"
						target="_blank"
					>
						{displayFilename}
					</a>
					{handleUpdate !== null && canEdit ? (
						<div className={Styles.FileButtons}>
							<EditMenuButton
								onClick={(): void => {
									setEditFile(file);
								}}
							>
								ファイル名を変更する
							</EditMenuButton>
						</div>
					) : null}
					{handleDestroy !== null && canDestroy(file, isVendor) && (
						<div className={Styles.FileButtons}>
							<DeleteMenuButton
								onClick={(): void => {
									setDeleteFile(file);
								}}
							>
								削除
							</DeleteMenuButton>
						</div>
					)}
				</div>
				<div className={Styles.Annotation}>
					{formatDate(file.createdAt, true)}に
					{fileAddName ? `${fileAddName}が` : null}登録
				</div>
			</div>
		);
	};
	return (
		<div className={Styles.DealCard}>
			{isLoading ? <Loading isLoading /> : null}
			<h3 className={Styles.Title}>
				{fileType}
				<div className={Styles.AddButtonWrapper}>
					<Flex alignItems="center" gap={8} marginLeft="auto">
						{createPdf !== null && (
							<NskButton onClick={onClickCreatePdf} size="sm" variant="normal">
								<RiFileTextLine className={Styles.Icon} />
								PDF作成
							</NskButton>
						)}
						{downloadExcel !== null && (
							<NskButton onClick={downloadExcel} size="sm" variant="normal">
								<RiDownloadLine className={Styles.Icon} />
								Excelダウンロード
							</NskButton>
						)}
						{handleUpload !== null && (
							<UploadButton onClick={onClickUpload} size="sm">
								<RiAddFill className={Styles.Icon} />
								追加
							</UploadButton>
						)}
					</Flex>
				</div>
			</h3>
			{description !== null && (
				<div className={Styles.Description}>{description}</div>
			)}
			<div className={Styles.FileList}>
				{files.map(renderRow)}
				{files.length === 0 && (
					<div className={Styles.NoFile}>ファイル登録なし</div>
				)}
			</div>
			<ConfirmModal
				confirmType="delete"
				handleAccept={(): Promise<void> => acceptDestroy()}
				handleCancel={(): void => {
					setDeleteFile(null);
				}}
				isOpen={deleteFile !== null}
			>
				ファイルを削除してよろしいですか？
			</ConfirmModal>
			<Modalv2 isOpen={editFile !== null} onClose={clearEditFile}>
				<ModalHeader title="表示用ファイル名の変更" />
				<ModalBody>
					<FormControl label="表示用ファイル名" variant="vertical">
						<Input
							defaultValue={
								editFile?.optionalFilename ? editFile.optionalFilename : ""
							}
							onChange={onChangeOptionalFilename}
							placeholder={
								editFile?.originalFilename ? editFile.originalFilename : ""
							}
						/>
					</FormControl>
				</ModalBody>
				<ModalFooter
					leftArea={
						<Button onClick={clearEditFile} variant="secondary">
							変更せず閉じる
						</Button>
					}
					rightArea={
						<Button onClick={acceptUpdate} variant="primary">
							変更
						</Button>
					}
				/>
			</Modalv2>
		</div>
	);
};
